// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Modal, TextInput, StatusBanner } from '@loa/ui-components';
import { advisorApi } from '../api/advisorApi';
import type { AdvisorProfile } from '../types/advisor';
import { AdvisorPhotoUpload } from './AdvisorPhotoUpload';

type CompleteProfileModalProps = Readonly<{
  advisor: AdvisorProfile;
  onSaved: (updated: AdvisorProfile) => void;
}>;

/**
 * Blocking modal shown to advisors whose profile is missing required
 * fields after SSO login. Phone number is the only required field in V1;
 * profile photo is offered alongside (optional). No skip — the modal
 * stays mounted until the advisor saves a non-blank phone number.
 *
 * The advisor can still sign out via the normal flow (the layout's
 * sign-out is reachable through /logout-page even if this modal is
 * blocking).
 */
export function CompleteProfileModal({ advisor, onSaved }: CompleteProfileModalProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(advisor.photoUrl);

  const handleSave = async () => {
    const trimmed = phoneNumber.trim();
    if (!trimmed) {
      setError('Phone number is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const updated = await advisorApi.updateProfile({ phoneNumber: trimmed });
      onSaved(updated);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    // open is forced true — closing requires a successful save (no onClose handler that actually unmounts)
    <Modal open onClose={() => { /* blocking */ }} size="medium">
      <Modal.Header icon="user" title="Complete your profile" />
      <Modal.Content>
        <p style={{ marginBottom: '1rem' }}>
          Welcome, {advisor.firstName}! Add a phone number to finish setting up
          your account. You can update other details later from Settings.
        </p>

        {error && (
          <StatusBanner
            variant="error"
            message={error}
            visible
            position="inline"
            dismissible={false}
          />
        )}

        <div className="flex flex-col gap-xl">
          <TextInput
            label="Phone number"
            type="tel"
            placeholder="e.g. +44 7700 900123"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            maxLength={50}
            required
            fluid
          />

          <div>
            <p style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
              Profile photo (optional)
            </p>
            <AdvisorPhotoUpload
              currentPhotoUrl={currentPhotoUrl}
              advisorId={advisor.id.toString()}
              onPhotoUpdated={(profile) => setCurrentPhotoUrl(profile.photoUrl)}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions
        primaryAction={{
          label: 'Save and continue',
          onClick: () => { void handleSave(); },
          loading: saving,
          disabled: saving,
        }}
      />
    </Modal>
  );
}
